import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Heart } from "lucide-react";
import { api, COLORS } from "@/lib/api";
import { MILESTONES, SOCIALS } from "@/data/siteContent";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};

const CARDS = [
  {
    color: "magenta",
    title: "Programas que transforman",
    text: "Acompañamos a las juventudes con formación, liderazgo e incidencia pública.",
    cta: "Conoce los programas",
    to: "/programas",
  },
  {
    color: "lime",
    title: "Una red que crece",
    text: "Tenemos presencia en 17 estados de la república mexicana e incidimos con una red de más de 60 clubs.",
    cta: "Únete a la red",
    to: "/red-de-clubs",
  },
  {
    color: "orange",
    title: "Tu apoyo cuenta",
    text: "Con tu donación sostenemos talleres, materiales y espacios seguros para más jóvenes.",
    cta: "Dona ahora",
    to: "/donaciones/dona-ahora",
  },
];

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [slide, setSlide] = useState(0);

  const BANNERS = [
    { img: "/concurso/banner-1.webp", to: "/programas/tejiendo-redes-ciudadanas", alt: "5ta edición Concurso Nacional — Tejiendo Redes Ciudadanas por Juventudes Diversas" },
    { img: "/jardin/banner-eljardin.png", to: "/programas/el-jardin", alt: "El Jardín — Newsletter de Juventudes Latentes" },
    { img: "/concurso/banner-calendario.webp", download: "/calendario-jl-2026.pdf", alt: "Calendario de Incidencia y Participación Juvenil 2026 — Haz click para descargarlo" },
  ];

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % BANNERS.length), 10000);
    return () => clearInterval(t);
  }, [BANNERS.length]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]">
        <div className="absolute inset-0">
          <img
            src="/hero.jpg"
            alt="Juventudes Latentes en evento"
            className="w-full h-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(17,17,17,0.55) 0%, rgba(17,17,17,0.45) 45%, rgba(17,17,17,0.75) 100%)",
            }}
          />
        </div>
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="relative max-w-4xl mx-auto px-6 py-24 md:py-32 text-center"
        >
          <motion.img
            src="/logo.png"
            alt="Logo Juventudes Latentes"
            data-testid="hero-logo"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="w-28 h-28 md:w-36 md:h-36 mx-auto mb-8 rounded-3xl shadow-2xl"
          />
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-white drop-shadow">
            JUVENTUDES <span className="text-gradient">LATENTES</span>
          </h1>
          <p className="mt-6 text-xl md:text-2xl text-white/90 leading-snug font-medium max-w-2xl mx-auto drop-shadow">
            10 años formando liderazgos que transforman a México.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link
              to="/conocenos"
              data-testid="hero-conocenos-btn"
              className="inline-flex items-center gap-2 bg-white text-brand-ink font-semibold px-6 py-3.5 rounded-sm hover:bg-brand-orange hover:text-white transition-colors duration-200"
            >
              Conócenos <ArrowRight size={18} />
            </Link>
            <Link
              to="/donaciones/dona-ahora"
              data-testid="hero-donate-btn"
              className="inline-flex items-center gap-2 border border-white text-white font-semibold px-6 py-3.5 rounded-sm hover:bg-white hover:text-brand-ink transition-colors duration-200"
            >
              <Heart size={17} /> Dona ahora
            </Link>
          </div>
        </motion.div>
      </section>

      {/* BANNER ROTATIVO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <div data-testid="home-banner-carousel" className="relative overflow-hidden rounded-2xl border border-[#E5E5E5]">
          {BANNERS.map((b, i) => {
            const cls = `block transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`;
            const inner = <img src={b.img} alt={b.alt} className="w-full h-auto" />;
            return b.download ? (
              <a
                key={i}
                href={b.download}
                download
                data-testid={`home-banner-slide-${i}`}
                className={cls}
                aria-hidden={i === slide ? "false" : "true"}
              >
                {inner}
              </a>
            ) : (
              <Link
                key={i}
                to={b.to}
                data-testid={`home-banner-slide-${i}`}
                className={cls}
                aria-hidden={i === slide ? "false" : "true"}
              >
                {inner}
              </Link>
            );
          })}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {BANNERS.map((b, i) => (
              <button
                key={i}
                data-testid={`home-banner-dot-${i}`}
                onClick={() => setSlide(i)}
                aria-label={`Ir al banner ${i + 1}`}
                className={`h-2.5 rounded-full transition-all duration-300 ${i === slide ? "w-8 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"}`}
                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.35)" }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 3 CARDS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <motion.div
              key={c.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={fadeUp}
            >
              <Link
                to={c.to}
                data-testid={`home-card-${c.color}`}
                className="group block h-full bg-white border border-brand-ink p-8 rounded-sm transition-all duration-300 hover:-translate-y-1"
                style={{ borderColor: "#1A1A1A" }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = COLORS[c.color])}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#1A1A1A")}
              >
                <div className="w-12 h-12 rounded-sm mb-6" style={{ background: COLORS[c.color] }} />
                <h3 className="font-heading text-2xl font-semibold tracking-tight text-brand-ink">
                  {c.title}
                </h3>
                <p className="mt-3 text-brand-ink/70 leading-relaxed">{c.text}</p>
                <span className="mt-6 inline-flex items-center gap-1 font-semibold text-brand-ink group-hover:gap-2 transition-all">
                  {c.cta} <ArrowUpRight size={18} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BLOG CAROUSEL — Voces y reflexiones */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="mb-10">
          <span className="text-sm uppercase tracking-[0.15em] font-semibold text-brand-magenta">
            Nuestro blog
          </span>
          <h2 className="mt-2 font-heading text-4xl md:text-5xl font-semibold tracking-tight text-brand-ink">
            Voces y reflexiones
          </h2>
        </div>

        {posts.length === 0 ? (
          <p className="text-brand-ink/50">Pronto compartiremos nuevas entradas.</p>
        ) : (
          <Carousel opts={{ align: "start", loop: posts.length > 3 }} className="w-full">
            <CarouselContent className="-ml-4">
              {posts.map((p, i) => (
                <CarouselItem
                  key={p.id}
                  data-testid={`blog-slide-${i}`}
                  className="pl-4 md:basis-1/2 lg:basis-1/3"
                >
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block h-full overflow-hidden rounded-sm border border-[#E5E5E5] bg-white"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={p.image_url}
                        alt={p.title}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <span
                        className="inline-block text-xs font-bold px-2.5 py-0.5 rounded-sm"
                        style={{
                          background: [COLORS.lime, COLORS.yellow, COLORS.orange][i % 3],
                          color: "#111",
                        }}
                      >
                        {p.tag}
                      </span>
                      <h3 className="mt-3 font-heading text-xl font-semibold tracking-tight text-brand-ink group-hover:text-brand-magenta transition-colors">
                        {p.title}
                      </h3>
                      <p className="mt-2 text-sm text-brand-ink/70 line-clamp-2">{p.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-ink">
                        Leer entrada <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </a>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="mt-6 flex gap-2">
              <CarouselPrevious className="static translate-y-0 rounded-sm border-brand-ink" data-testid="blog-prev" />
              <CarouselNext className="static translate-y-0 rounded-sm border-brand-ink" data-testid="blog-next" />
            </div>
          </Carousel>
        )}
      </section>

      {/* MILESTONES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <span className="text-sm uppercase tracking-[0.15em] font-semibold text-brand-orange">
          Trayectoria
        </span>
        <h2 className="mt-2 font-heading text-4xl md:text-5xl font-semibold tracking-tight text-brand-ink mb-12">
          Nuestra historia
        </h2>
        <div className="relative pl-8 border-l border-[#E5E5E5]">
          {MILESTONES.map((m, i) => (
            <motion.div
              key={m.year}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={fadeUp}
              className="relative pb-10 last:pb-0"
            >
              <span
                className="absolute -left-[41px] top-1 w-4 h-4 rounded-full ring-4 ring-brand-cream"
                style={{ background: COLORS[m.color] }}
              />
              <span className="font-mono text-sm text-brand-ink/50">{m.year}</span>
              <h3 className="mt-1 font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-ink">
                {m.title}
              </h3>
              <p className="mt-1 text-brand-ink/70 max-w-2xl">{m.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SOCIAL CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="bg-brand-ink rounded-sm p-10 md:p-16 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white">
            Conversemos en redes
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {SOCIALS.map((soc) => (
              <a
                key={soc.name}
                href={soc.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`home-social-${soc.name.toLowerCase()}`}
                className="border border-white/30 text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-white hover:text-brand-ink transition-colors duration-200"
              >
                {soc.name}
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
